// pages/Bills/BillSuccessPage.jsx — FIXED + THEMED (instant-resolution pass)
// ─────────────────────────────────────────────────────────────
// FIXED (this pass): NOT INSTANT. The poll used setInterval(..., 30_000)
// exclusively — setInterval's first tick fires only AFTER the full
// delay, never immediately on mount. So even when an order had ALREADY
// resolved (webhook fired before the user ever landed on this page, or
// resolved while they were reading the page), the UI still sat on
// "Order being processed…" for up to a full 30 seconds before the
// first check ever ran. That's not "stuck forever," but it reads as
// stuck to a user watching the screen, and it's not the "instant and
// automatic" behavior wanted.
//
// FIX: extracted the status-check logic into a standalone
// checkStatusOnce() function, called once IMMEDIATELY when entering
// the processing state (in addition to being scheduled on the
// interval as before). If the order is already resolved by the time
// this page mounts or re-enters "processing", the very first check
// (synchronous with mount, not 30s later) catches it and the UI
// updates right away — no refresh needed, no waiting out the interval.
// The 30-second interval remains as the ongoing fallback for orders
// that are genuinely still in flight.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation }    from "react-router-dom";
import html2canvas from "html2canvas";
import { checkBillStatus } from "../../api/apiServices";
import { useSocket } from "../../hooks/useSocket";
import { useAuth } from "../../context/useAuth";

// Icon tone mapped onto the semantic token scale instead of fixed hexes,
// so the icon chip background/foreground adapts correctly in dark mode.
const TYPE_CONFIG = {
  airtime:     { icon: "ri-phone-line",     tone: "success", label: "Airtime Purchase" },
  data:        { icon: "ri-wifi-line",       tone: "info",    label: "Data Bundle"      },
  electricity: { icon: "ri-flashlight-line", tone: "warning", label: "Electricity Bill" },
  tv:          { icon: "ri-tv-2-line",       tone: "purple",  label: "TV Subscription"  },
  internet:    { icon: "ri-router-line",     tone: "success", label: "Internet Bill"    },
  betting:     { icon: "ri-gamepad-line",    tone: "danger",  label: "Betting Wallet"   },
};

// Normalizes the full set of status strings actually observed in the
// database down to the states this page's UI cares about. Confirmed
// against checkBillStatusController.js: that endpoint only ever
// returns "completed" | "refunded" | "failed" | "processing" as
// txStatus — but state.txStatus (passed via router state from the
// purchase flow) may still arrive as "success"/"pending" from older
// controller code paths, so both vocabularies are normalized here.
function normalizeTxStatus(raw) {
  const s = (raw || "").toLowerCase();
  if (s === "completed" || s === "success")   return "completed";
  if (s === "pending"   || s === "processing") return "processing";
  if (s === "failed")                          return "failed";
  if (s === "refunded")                        return "refunded";
  return "processing"; // unknown status — safer to keep polling than to claim success
}

// ── Processing banner — shown for ALL bill types when still pending ──
function ProcessingBanner({ type, transactionId }) {
  const isElec = type === "electricity";
  return (
    <div className="bg-info-light border-2 border-info-border rounded-2xl p-4 mb-4 text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-info)", borderTopColor: "transparent" }} />
        <p className="text-info-text text-sm font-semibold">
          {isElec ? "Token being generated…" : "Order being processed…"}
        </p>
      </div>
      <p className="text-info-text text-xs leading-relaxed mb-2 opacity-90">
        {isElec
          ? "Your payment was received. The electricity provider is generating your token. This usually takes 1–5 minutes."
          : "Your payment was received and your order is being processed by the provider. This usually takes 1–5 minutes."}
        {" "}<strong className="text-info-text">You do not need to pay again.</strong>
      </p>
      <p className="text-info-text text-xs opacity-70">This page checks automatically every 30 seconds.</p>
    </div>
  );
}

// ── Electricity token card — only when token is ready ──────────
function ElectricityTokenCard({ token, units }) {
  return (
    <div className="bg-warning-light border-2 border-warning-border rounded-2xl p-4 mb-4 text-center">
      <p className="text-warning-text text-xs font-semibold uppercase tracking-wide mb-2">⚡ Your Meter Token</p>
      <p className="text-warning-text font-mono font-bold text-2xl tracking-widest break-all">{token}</p>
      {units && <p className="text-warning-text text-xs font-bold mt-1">{units} units</p>}
      <p className="text-warning-text text-xs mt-2 opacity-70">Enter this token on your prepaid meter</p>
      <button
        onClick={() => navigator.clipboard?.writeText(token).then(() => alert("Token copied!"))}
        className="mt-3 text-warning-text text-xs font-semibold border border-warning-border px-4 py-1.5 rounded-xl"
      >
        <i className="ri-file-copy-line mr-1" />Copy Token
      </button>
    </div>
  );
}

// ── Refunded banner ────────────────────────────────────────────
function RefundedBanner() {
  return (
    <div className="bg-danger-light border-2 border-danger-border rounded-2xl p-4 mb-4 text-center">
      <i className="ri-refund-2-line text-danger text-2xl mb-2 block" />
      <p className="text-danger-text text-sm font-semibold mb-1">Order Refunded</p>
      <p className="text-danger-text text-xs opacity-80">The provider could not complete this order. Your wallet has been refunded.</p>
    </div>
  );
}

// ── Timeout banner (still processing after 10 min) ─────────────
function TimeoutBanner({ transactionId }) {
  return (
    <div className="bg-warning-light border-2 border-warning-border rounded-2xl p-4 mb-4 text-center">
      <i className="ri-time-line text-warning text-2xl mb-2 block" />
      <p className="text-warning-text text-sm font-semibold mb-1">Still processing</p>
      <p className="text-warning-text text-xs leading-relaxed opacity-90">
        The provider is taking longer than usual. Your order has been received and will be
        completed automatically — check your transaction history for updates.
      </p>
      <button
        onClick={() => window.location.href = `/transaction/${transactionId}`}
        className="mt-3 text-warning-text text-xs font-semibold border border-warning-border px-4 py-1.5 rounded-xl"
      >
        View Transaction
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function BillSuccessPage() {
  const navigate        = useNavigate();
  const { state }       = useLocation();
  const { user }        = useAuth(); // IMPORTANT: verify this matches the auth hook actually used elsewhere in this app — see note below.
  const { on, off }     = useSocket(user?.id || user?._id);
  const hasNavigated    = useRef(false);
  const cardRef         = useRef(null);
  const pollRef         = useRef(null);
  const pollCount       = useRef(0);
  const isCheckingRef   = useRef(false); // guards against overlapping checks
  const MAX_POLLS       = 20; // 20 × 30s = 10 min

  // ── Derive initial state from backend response ────────────
  // state.txStatus: "completed" | "success" | "processing" | "pending" | "failed"
  // state.token: electricity token if delivered immediately
  const initialTxStatus = normalizeTxStatus(state?.txStatus || "completed");
  const [liveToken,   setLiveToken]   = useState(state?.token || null);
  const [liveUnits,   setLiveUnits]   = useState(state?.units || null);
  // orderStatus: "completed" | "processing" | "refunded" | "failed" | "timeout"
  const [orderStatus, setOrderStatus] = useState(initialTxStatus);

  useEffect(() => {
    if (!state && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate("/bills");
    }
  }, [state, navigate]);

  // FIXED — extracted so it can be called both immediately (on entering
  // "processing") and on the recurring interval, instead of only ever
  // living inside setInterval's callback where it could never run
  // before the first 30s delay elapsed.
  const checkStatusOnce = useCallback(async () => {
    if (isCheckingRef.current || !state?.transactionId) return;
    isCheckingRef.current = true;
    pollCount.current += 1;

    try {
      const res = await checkBillStatus(state.transactionId);

      if (res.resolved) {
        const resolvedStatus = normalizeTxStatus(res.txStatus);

        if (resolvedStatus === "completed") {
          if (res.token) {
            setLiveToken(res.token);
            setLiveUnits(res.units || null);
          }
          setOrderStatus("completed");
          clearInterval(pollRef.current);
        } else if (resolvedStatus === "refunded") {
          setOrderStatus("refunded");
          clearInterval(pollRef.current);
        } else if (resolvedStatus === "failed") {
          setOrderStatus("failed");
          clearInterval(pollRef.current);
        }
        // resolvedStatus === "processing" → res.resolved was true but
        // status is still non-terminal; fall through and keep polling.
      } else if (pollCount.current >= MAX_POLLS) {
        setOrderStatus("timeout");
        clearInterval(pollRef.current);
      }
    } catch {
      if (pollCount.current >= MAX_POLLS) {
        setOrderStatus("timeout");
        clearInterval(pollRef.current);
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [state?.transactionId]);

  // ── Poll for any bill type when processing ────────────────
  useEffect(() => {
    if (orderStatus !== "processing" || !state?.transactionId) return;

    // FIXED — check immediately on entering "processing", instead of
    // waiting for setInterval's first 30s tick. This is what makes an
    // already-resolved order (webhook fired before the page loaded, or
    // while the user was reading the receipt) reflect instantly
    // instead of sitting on the spinner for up to 30 more seconds.
    checkStatusOnce();

    pollRef.current = setInterval(checkStatusOnce, 30_000);

    return () => clearInterval(pollRef.current);
  }, [orderStatus, state?.transactionId, checkStatusOnce]);

  // FIXED — also re-check whenever the tab regains focus or becomes
  // visible again. Covers the case where a user switches away (e.g.
  // to check their bank SMS) and comes back — the order may have
  // resolved while the tab was backgrounded, and this catches it
  // immediately on return instead of waiting for the next 30s tick.
  useEffect(() => {
    if (orderStatus !== "processing") return;

    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkStatusOnce();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", checkStatusOnce);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", checkStatusOnce);
    };
  }, [orderStatus, checkStatusOnce]);

  // FIXED — instant push update via Socket.IO, in addition to the
  // polling fallback above. The backend (airtimenigeriaWebhook.js,
  // billRequeryCron.js's forceRefund, checkBillStatusController.js)
  // now emits a "transactionUpdate" event the moment any of those
  // resolve THIS transaction — see services/transactionEvents.js.
  // This is the actual "instant, no refresh needed" path; the polling
  // effect above remains as a safety net for when the socket is
  // disconnected, reconnecting, or unsupported in some environment.
  useEffect(() => {
    if (orderStatus !== "processing" || !state?.transactionId) return;

    const handleUpdate = (payload) => {
      // Only react to updates for THIS transaction — the socket
      // delivers to the user's room, which could in principle include
      // updates for other transactions if the user has multiple
      // orders processing at once.
      if (payload.transactionId !== state.transactionId) return;

      const resolvedStatus = normalizeTxStatus(payload.status);
      console.log("[BillSuccessPage] socket transactionUpdate:", payload);

      if (resolvedStatus === "completed") {
        if (payload.token) {
          setLiveToken(payload.token);
          setLiveUnits(payload.units || null);
        }
        setOrderStatus("completed");
        clearInterval(pollRef.current);
      } else if (resolvedStatus === "refunded") {
        setOrderStatus("refunded");
        clearInterval(pollRef.current);
      } else if (resolvedStatus === "failed") {
        setOrderStatus("failed");
        clearInterval(pollRef.current);
      }
    };

    on("transactionUpdate", handleUpdate);
    return () => off("transactionUpdate", handleUpdate);
  }, [orderStatus, state?.transactionId, on, off]);

  // FIXED — same bug as TransactionDetail.jsx's shareReceipt: the
  // button never reflected any state, and canvas.toBlob()'s callback
  // was fire-and-forget — nothing in the surrounding async function
  // actually awaited it, so there was no reliable point at which
  // "done" could even be known, let alone shown on the button. After
  // a successful download the button still read "Share Receipt as
  // Image" because nothing ever changed it. See
  // TransactionDetail.jsx's identical fix for the full explanation.
  const [shareStatus, setShareStatus] = useState("idle");

  const canvasToBlob = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to generate image from receipt"));
      }, "image/png");
    });

  const shareReceipt = async () => {
    if (!cardRef.current || shareStatus === "working") return;
    setShareStatus("working");

    try {
      // Receipt card is an intentionally light surface regardless of
      // theme, so the screenshot background stays white-on-paper here too.
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false });
      const blob = await canvasToBlob(canvas);
      const file = new File([blob], "easwallet-receipt.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ title: "Easwallet Receipt", files: [file] });
        } catch (shareErr) {
          // Cancelling the native share sheet is not a failure —
          // AbortError — just go back to idle without an error state.
          if (shareErr?.name !== "AbortError") {
            downloadFallback(canvas);
          } else {
            setShareStatus("idle");
            return;
          }
        }
      } else {
        downloadFallback(canvas);
      }

      setShareStatus("done");
      setTimeout(() => setShareStatus("idle"), 2500);
    } catch (err) {
      console.error("shareReceipt error:", err);
      const text = `Easwallet Receipt\nAmount: ₦${Number(state?.amount).toLocaleString()}\nRef: ${state?.transactionId || ""}\nDate: ${new Date().toLocaleString("en-NG")}`;
      try {
        await navigator.share?.({ title: "Easwallet Receipt", text });
        setShareStatus("done");
        setTimeout(() => setShareStatus("idle"), 2500);
      } catch {
        setShareStatus("error");
        setTimeout(() => setShareStatus("idle"), 3000);
      }
    }
  };

  const downloadFallback = (canvas) => {
    const a = document.createElement("a");
    a.download = "easwallet-receipt.png";
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  if (!state) return null;

  const { type, title, detail, subDetail, amount, requestId, transactionId, reuse } = state;
  const cfg        = TYPE_CONFIG[type] || TYPE_CONFIG.airtime;
  const displayRef = transactionId || requestId || "N/A";
  const timeStr    = new Date().toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const isProcessing = orderStatus === "processing";
  const isCompleted  = orderStatus === "completed";

  return (
    <div className="min-h-screen bg-bg flex flex-col">

      {/* ── Header — changes based on real status ──────────── */}
      <div className="px-5 pt-16 pb-10 text-center relative overflow-hidden"
        style={{ background: isProcessing
          ? "linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)"    // status gradients are deliberately fixed —
          : "var(--gradient-hero)" }}                              // success state reuses the app's hero gradient
      >
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(30%,-30%)" }} />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(-30%,30%)" }} />
        <div className="relative z-10">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white/30">
            {isProcessing
              ? <div className="w-8 h-8 border-4 border-white/40 border-t-white rounded-full animate-spin" />
              : <i className="ri-checkbox-circle-fill text-white text-4xl" />}
          </div>
          <h1 className="text-white text-2xl font-bold mb-1 font-display">
            {isProcessing ? "Payment Received!" : "Payment Successful!"}
          </h1>
          <p className="text-sm" style={{ color: isProcessing ? "#BFDBFE" : "var(--gradient-hero-text-muted)" }}>
            {isProcessing
              ? "Your order is being processed by the provider"
              : "Your bill has been paid"}
          </p>
          <div className="mt-4">
            <p className="text-sm" style={{ color: isProcessing ? "#BFDBFE" : "var(--gradient-hero-text-muted)" }}>Amount Paid</p>
            <p className="text-4xl font-bold mt-1 text-white font-display">
              ₦{Number(amount).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 px-5 -mt-5 pb-10">

        {/* ── Status banners ──────────────────────────────────── */}
        {orderStatus === "processing" && (
          <ProcessingBanner type={type} transactionId={transactionId} />
        )}
        {orderStatus === "timeout" && (
          <TimeoutBanner transactionId={transactionId} />
        )}
        {orderStatus === "refunded" && <RefundedBanner />}

        {/* Electricity token — only when ready */}
        {type === "electricity" && isCompleted && liveToken && (
          <ElectricityTokenCard token={liveToken} units={liveUnits} />
        )}

        {/* Electricity token still missing after completion — shouldn't happen but handle it */}
        {type === "electricity" && isCompleted && !liveToken && (
          <div className="bg-warning-light border border-warning-border rounded-2xl p-3 mb-4 text-center">
            <p className="text-warning-text text-xs">Token not in response — check transaction history or contact support.</p>
          </div>
        )}

        {/* ── Receipt card — intentionally light/paper-like in both
             themes; see file header note. Uses raw hex rather than
             surface tokens on purpose. ──────────────────────── */}
        <div ref={cardRef} className="bg-white rounded-3xl border border-black/5 shadow-lg overflow-hidden mb-4">
          <div className="relative h-6 bg-bg">
            <div className="absolute -bottom-3 left-0 right-0 h-6 bg-white rounded-t-3xl" />
            <div className="absolute bottom-0 -left-3 w-6 h-6 bg-bg rounded-full" />
            <div className="absolute bottom-0 -right-3 w-6 h-6 bg-bg rounded-full" />
          </div>
          <div className="px-5 pb-6 pt-2">
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `var(--color-${cfg.tone}-light)` }}>
                <i className={`${cfg.icon} text-xl`} style={{ color: `var(--color-${cfg.tone})` }} />
              </div>
            </div>
            <div className="space-y-0">
              {[
                { label: "Service",    value: title },
                { label: "Recipient",  value: detail },
                subDetail && { label: "Account",    value: subDetail },
                type === "electricity" && liveToken && { label: "Token", value: liveToken },
                { label: "Date & Time", value: timeStr },
                { label: "Reference",   value: displayRef },
                { label: "Status",      value: isProcessing ? "⏳ Processing" : "✅ Successful" },
              ].filter(Boolean).map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2.5 border-b border-black/5">
                  <span className="text-slate-400 text-sm">{row.label}</span>
                  <span className="text-slate-800 font-semibold text-sm text-right max-w-[55%] break-all">{row.value}</span>
                </div>
              ))}
            </div>
            {/* FIXED — reflects shareStatus instead of a static label.
                Kept in the same fixed slate/light palette as the rest
                of this receipt card (not switched to theme tokens),
                consistent with this card's deliberate "paper" styling
                regardless of app theme — see the file header note on
                why the receipt stays light in both light and dark mode. */}
            <button
              onClick={shareReceipt}
              disabled={shareStatus === "working"}
              className={`w-full mt-5 border-2 py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${
                shareStatus === "done"
                  ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                  : shareStatus === "error"
                  ? "border-red-200 text-red-600 bg-red-50"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {shareStatus === "working" ? (
                <><div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />Preparing receipt…</>
              ) : shareStatus === "done" ? (
                <><i className="ri-checkbox-circle-line" />Receipt saved</>
              ) : shareStatus === "error" ? (
                <><i className="ri-error-warning-line" />Failed — tap to retry</>
              ) : (
                <><i className="ri-image-line" />Share Receipt as Image</>
              )}
            </button>
          </div>
        </div>

        {/* ── Actions ──────────────────────────────────────── */}
        {reuse && isCompleted && (
          <button
            onClick={() => {
              const path = type === "electricity" ? "/bills/electricity" : type === "tv" ? "/bills/tv" : "/bills";
              navigate(path, { state: { reuse } });
            }}
            className="w-full bg-brand hover:bg-brand-hover text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 mb-3"
          >
            Pay Again for Same {type === "electricity" ? "Meter" : type === "tv" ? "Smartcard" : "Number"}
          </button>
        )}
        <button
          onClick={() => navigate("/bills")}
          className={`w-full py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 mb-3 ${
            reuse && isCompleted
              ? "border-2 border-brand-border text-brand-text bg-brand-light"
              : "bg-brand hover:bg-brand-hover text-on-brand"
          }`}
        >
          Pay Another Bill
        </button>
        <button onClick={() => navigate("/home")} className="w-full text-muted py-3 text-sm font-medium">
          Back to Home
        </button>
      </div>
    </div>
  );
}
